import React, { useEffect, useState } from 'react';
import { SectionHeader, Callout } from '../components/UI';
import { api, moneyFull } from '../lib/api';
import { BellDot } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BUCKET_COLOR = {
  Leadership: 'bg-indie-navy text-indie-cream',
  Marketing: 'bg-indie-cobalt text-white',
  Sales: 'bg-indie-evergreen text-white',
  Fulfillment: 'bg-indie-cream text-indie-navy',
  Overhead: 'bg-indie-ink text-indie-cream',
};

export default function TeamRoster() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [gustoStatus, setGustoStatus] = useState(null);
  const [teamSheetStatus, setTeamSheetStatus] = useState(null);

  useEffect(() => {
    api.get('/team').then((r) => setTeam(r.data));
    api.get('/integrations/status').then((r) => {
      setGustoStatus(r.data?.gusto);
      setTeamSheetStatus(r.data?.team_sheet);
    }).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const byDept = team.reduce((acc, p) => { (acc[p.department] = acc[p.department] || []).push(p); return acc; }, {});
  const newHires = team.filter((p) => p.new_hire_alert);
  const isGustoLive = gustoStatus?.status === 'connected';
  const isSheetLive = teamSheetStatus?.status === 'connected' && team.some((p) => p.source === 'sheets');
  const isLive = isSheetLive || isGustoLive;
  const sourceLabel = isSheetLive ? 'Team · Google Sheet (live)' : isGustoLive ? 'Team · Gusto (live)' : 'Team · mock roster';
  const w2Count = team.filter((p) => p.type === 'W-2').length;
  const count1099 = team.filter((p) => p.type === '1099').length;

  const connectGusto = async () => {
    try {
      const { data } = await api.get('/integrations/gusto/authorize');
      window.location.href = data.authorize_url;
    } catch {
      /* handled on the Integrations page */
    }
  };

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow={sourceLabel}
        title="Who we are"
        description={`${team.length} teammates · ${w2Count} W-2 · ${count1099} contractors — mapped into the P&L expense buckets.`}
      />

      {!isLive && user?.is_admin && (
        <Callout title={gustoStatus?.status === 'needs_auth' ? 'Connect Gusto to see the real roster' : 'Running on mock roster data'} tone={gustoStatus?.status === 'needs_auth' ? 'cta' : 'default'}>
          {gustoStatus?.status === 'needs_auth' ? (
            <div className="flex items-center gap-4 flex-wrap">
              <p className="text-sm">Credentials are loaded — authorize Gusto to replace this list with live employees and contractors.</p>
              <button
                data-testid="team-connect-gusto-btn"
                onClick={connectGusto}
                className="bg-indie-yellow text-indie-ink font-semibold px-4 py-2 hover:bg-indie-yellow/85"
              >
                Connect Gusto
              </button>
            </div>
          ) : (
            <p className="text-sm">
              Gusto isn't configured yet. Head to <span className="font-semibold">Integrations</span> and follow the walk-through to wire up
              the roster sync. Until then, the roster below is seeded mock data.
            </p>
          )}
        </Callout>
      )}

      {newHires.length > 0 && (
        <Callout title="New hires to reflect in the forecast" tone="alert">
          <div className="flex items-start gap-3">
            <BellDot className="text-red-600 mt-0.5" size={18} />
            <div>
              {newHires.map((p) => (
                <div key={p.id} className="text-sm">
                  <span className="font-semibold">{p.name}</span> joined as <span className="italic">{p.role}</span> on {p.start_date} — {moneyFull(p.monthly_comp)}/mo. Update the {p.bucket} assumption if not already reflected.
                </div>
              ))}
            </div>
          </div>
        </Callout>
      )}

      {Object.entries(byDept).map(([dept, people]) => (
        <div key={dept}>
          <div className="label-editorial mb-3">{dept} · {people.length}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {people.map((p) => {
              const initials = p.name.split(' ').map((n) => n[0]).slice(0, 2).join('');
              const badge = BUCKET_COLOR[p.bucket] || 'bg-indie-navy text-indie-cream';
              return (
                <div key={p.id} className="bg-white border-t-4 border-indie-navy p-5 flex items-start gap-4" data-testid={`team-member-${p.id}`}>
                  <div className={`h-12 w-12 flex items-center justify-center font-semibold text-sm ${badge}`}>{initials}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-indie-navy">{p.name}</div>
                    <div className="text-xs text-indie-ink/70 mt-0.5">{p.role}</div>
                    <div className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-wider text-indie-ink/50">
                      {p.start_date && <><span>Since {p.start_date}</span><span>·</span></>}
                      <span>{p.type}</span>
                    </div>
                    <div className="mt-1 text-xs num-tabular text-indie-evergreen">{p.comp_raw ? `${p.comp_raw}/mo · ` : (p.monthly_comp ? `${moneyFull(p.monthly_comp)}/mo · ` : '')}maps to {p.bucket}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
